/**
 * /frontend/src/features/analytics/pages/StaffingAnalyticsPage.js
 */

import React, { useEffect, useMemo } from 'react';
import useStaffingAnalytics from '../hooks/useStaffingAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import DemandForecastChart from '../components/staffing/DemandForecastChart';
import StaffRecommendationChart from '../components/staffing/StaffRecommendationChart';
import StaffEfficiencyChart from '../components/staffing/StaffEfficiencyChart';
import ForecastVsActualChart from '../components/staffing/ForecastVsActualChart';
import {
  toStaffingEfficiencySeries,
  toStaffingForecastSeries,
  toStaffingScheduleStack
} from '../utils/chartDataTransformers';
import './StaffingAnalyticsPage.css';

const onlyWithActuals = (rows) => (rows || []).filter((r) => Number(r?.actualJoins || 0) > 0);

/**
 * Staffing analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const StaffingAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const hook = useStaffingAnalytics({ request, storeToken, storeUuid, startDate, endDate, days: 7, date: endDate, refreshSignal });
  const forecastSeries = useMemo(() => toStaffingForecastSeries(hook.data?.forecast), [hook.data?.forecast]);
  const forecastVsActual = useMemo(() => onlyWithActuals(forecastSeries), [forecastSeries]);
  const efficiencySeries = useMemo(() => toStaffingEfficiencySeries(hook.data?.efficiency), [hook.data?.efficiency]);
  const scheduleStack = useMemo(() => toStaffingScheduleStack(hook.data?.schedule), [hook.data?.schedule]);
  const syncing = hook.isLoading || hook.isRefreshing;

  const isEmpty = useMemo(
    () => forecastSeries.length === 0 && efficiencySeries.length === 0 && scheduleStack.stacked.length === 0,
    [efficiencySeries.length, forecastSeries.length, scheduleStack.stacked.length]
  );

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No staffing analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <DemandForecastChart data={forecastSeries} />
      <div className="analytics-page-grid-2">
        <StaffRecommendationChart data={scheduleStack.stacked} zoneKeys={scheduleStack.zoneKeys} />
        <StaffEfficiencyChart data={efficiencySeries} />
      </div>
      <ForecastVsActualChart data={forecastVsActual} />
    </div>
  );
};

export default StaffingAnalyticsPage;
